import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
} from '@mui/material';
import { PersonSearch as PersonSearchIcon, Close as CloseIcon } from '@mui/icons-material';

import { getUsersList } from '../../API/usersApi';
import { listCustomerUsers } from '../../API/customerUsersApi';
import { getMimicUser, setMimicUser, clearMimicUser } from '../../utils/mimic';

const Wrapper = styled(Box)`
  ${(props) => props.theme.breakpoints.up('md')} {
    min-width: 280px;
  }
  min-width: 200px;
`;

const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

const MimicSelector = () => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounced = useDebouncedValue(input, 250);

  const active = useMemo(() => getMimicUser(), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      getUsersList({ search: debounced, page: 0, limit: 20 }).catch(() => ({ users: [] })),
      listCustomerUsers({ search: debounced, page: 0, limit: 20 }).catch(() => ({ users: [] })),
    ])
      .then(([internal, customers]) => {
        if (!alive) return;
        const internalOpts = (internal?.users || []).map((u) => ({
          id: u.id,
          type: 'internal',
          label: u.name || u.email || `Användare #${u.id}`,
          subtitle: [u.email, u.role].filter(Boolean).join(' · '),
          role: u.role,
          email: u.email,
        }));
        const customerOpts = (customers?.users || []).map((u) => ({
          id: u.id,
          type: 'customer',
          label: u.name || u.email || `Kund #${u.id}`,
          subtitle: [u.email, u.companyName || u.company_name].filter(Boolean).join(' · '),
          customerId: u.customer_id,
          email: u.email,
        }));
        setOptions([...internalOpts, ...customerOpts]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [debounced]);

  const handleSelect = (_e, value) => {
    if (!value || typeof value === 'string') return;
    setMimicUser({
      id: value.id,
      type: value.type,
      label: value.label,
      email: value.email,
      role: value.type === 'customer' ? 'customer' : value.role,
      customerId: value.customerId,
    });
  };

  if (active) {
    return (
      <Wrapper>
        <Chip
          color="warning"
          icon={<PersonSearchIcon />}
          label={`Mimic: ${active.label || `${active.type} #${active.id}`}`}
          onDelete={clearMimicUser}
          deleteIcon={<CloseIcon />}
          sx={{ maxWidth: '100%' }}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Autocomplete
        size="small"
        options={options}
        filterOptions={(x) => x}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
        groupBy={(o) => (o.type === 'internal' ? 'Internal users' : 'Customer users')}
        loading={loading}
        onInputChange={(_e, val) => setInput(val)}
        onChange={handleSelect}
        renderOption={(props, option) => (
          <li {...props} key={`${option.type}-${option.id}`}>
            <Box>
              <Box component="span" sx={{ fontWeight: 500 }}>
                {option.label}
              </Box>
              {option.subtitle && (
                <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: 12 }}>
                  {option.subtitle}
                </Box>
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Mimic user..."
            variant="outlined"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.08)',
              borderRadius: 1,
              input: { color: 'inherit' },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <PersonSearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Wrapper>
  );
};

export default MimicSelector;
